import addresses from '@/api/addresses'
import bind from '@/api/bind'
import * as types from '@/store/mutation-types'
import { formatAddressMasks } from '@/utils/format-addresses'
import formatDateData from '@/utils/formatDateData'

/**
 * @fileoverview this module contains the list of consumer shipping addresses
 * Anything that logically relates to shipping addresses belongs here.
 */

const state = []

const actions = {
  /**
   * Adds a shipping address using the bind service.
   *
   * @param {Vuex} store
   * @param {Object} shippingAddress
   */
  async addShippingAddress({ commit, dispatch }, shippingAddress) {
    try {
      const { data } = await bind({ shippingAddress })

      const payload = { maskedShippingAddress: data.maskedShippingAddress, shippingAddress }
      commit(types.ADD_SHIPPING_ADDRESS, payload)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Deletes a shipping address
   *
   * @param {Vuex} store
   * @param {String} shippingAddressId
   */
  async deleteShippingAddress({ commit, dispatch }, shippingAddressId) {
    try {
      await addresses.deleteShippingAddress(shippingAddressId)

      commit(types.DELETE_SHIPPING_ADDRESS, shippingAddressId)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * resets the module back to default
   * @param {Vuex} store
   */
  reset({ commit }) {
    commit(types.SET_SHIPPING_ADDRESSES, undefined)
  }
}

const mutations = {
  /**
   * Adds the given shipping address to the state, overrides masked data with user entered data.
   *
   * @param {Object} state
   * @param {Object} payload
   * @param {Object} payload.maskedShippingAddress
   * @param {Object} payload.shippingAddress
   */
  [types.ADD_SHIPPING_ADDRESS](state, { maskedShippingAddress, shippingAddress }) {
    const address = {
      ...maskedShippingAddress,
      ...shippingAddress,
      id: maskedShippingAddress.shippingAddressId,
      fullName: `${shippingAddress.firstName} ${shippingAddress.lastName}`,
      subdivision: shippingAddress.state,
      postalCode: shippingAddress.zip,
      cityTown: shippingAddress.city
    }

    state.push(formatDateData(address))
  },

  /**
   * Sets the initial data to the state.
   *
   * @param {Object} state
   * @param {Array} shippingAddresses
   */
  [types.SET_SHIPPING_ADDRESSES](state, shippingAddresses) {
    shippingAddresses = shippingAddresses || []

    // clears out the array and triggers Vue reactivity without reassigning the reference
    state.splice(0)

    shippingAddresses.forEach((address) => {
      state.push(formatAddressMasks(formatDateData(address)))
    })
  },

  /**
   * Removes the shipping address having the given id from the state.
   *
   * @param {Object} state
   * @param {String} addressId
   */
  [types.DELETE_SHIPPING_ADDRESS](state, addressId) {
    const addressIndex = state.findIndex(({ id }) => id === addressId)

    if (addressIndex > -1) {
      state.splice(addressIndex, 1)
    }
  }
}

export default {
  state,
  actions,
  mutations
}
